/**
 * Safe logging utilities to prevent sensitive data from being logged
 * This prevents passwords, tokens, and other credentials from appearing in console logs
 */

interface SensitiveData {
  password?: string;
  confirm_password?: string;
  auth_token?: string;
  token?: string;
  secret?: string;
  api_key?: string;
  private_key?: string;
  access_token?: string;
  refresh_token?: string;
  client_secret?: string;
  session_id?: string;
  [key: string]: any;
}

/**
 * Masks sensitive fields in an object for safe logging
 * @param data - The data object that might contain sensitive information
 * @returns A copy of the data with sensitive fields masked
 */
export function maskSensitiveData<T extends Record<string, any>>(data: T): T {
  const masked = { ...data };
  
  // List of fields that should always be masked
  const sensitiveFields = [
    'password',
    'confirm_password', 
    'auth_token',
    'token',
    'secret',
    'api_key',
    'private_key',
    'access_token',
    'refresh_token',
    'client_secret',
    'session_id'
  ] as const;
  
  sensitiveFields.forEach(field => {
    if (field in masked && masked[field] !== undefined) {
      (masked as any)[field] = '***MASKED***';
    }
  });
  
  return masked;
}

/**
 * Safely logs data by masking sensitive information
 * Only logs if verbose mode is enabled
 * @param message - The log message
 * @param data - The data to log (will be masked if it contains sensitive fields)
 */
export function safeLog(message: string, data?: any): void {
  // Import here to avoid circular dependencies
  const { isVerboseMode } = require('./config');
  
  if (!isVerboseMode()) {
    return; // Skip logging in non-verbose mode
  }
  
  if (data) {
    const maskedData = maskSensitiveData(data);
    console.log(message, maskedData);
  } else {
    console.log(message);
  }
}

/**
 * Safely logs errors by masking sensitive information
 * @param message - The error message
 * @param error - The error object or data (will be masked if it contains sensitive fields)
 */
export function safeErrorLog(message: string, error?: any): void {
  if (error) {
    const maskedError = maskSensitiveData(error);
    console.error(message, maskedError);
  } else {
    console.error(message);
  }
}

/**
 * Safely logs warnings by masking sensitive information
 * Only logs if verbose mode is enabled
 * @param message - The warning message
 * @param data - The data to log (will be masked if it contains sensitive fields)
 */
export function safeWarnLog(message: string, data?: any): void {
  // Import here to avoid circular dependencies
  const { isVerboseMode } = require('./config');
  
  if (!isVerboseMode()) {
    return; // Skip logging in non-verbose mode
  }
  
  if (data) {
    const maskedData = maskSensitiveData(data);
    console.warn(message, maskedData);
  } else {
    console.warn(message);
  }
}

/**
 * Safely logs API calls by masking sensitive data in request bodies
 * Only logs if verbose mode is enabled
 * @param endpoint - The API endpoint being called
 * @param config - The request configuration (body will be masked if it contains sensitive fields)
 */
export function safeApiLog(endpoint: string, config: any): void {
  // Import here to avoid circular dependencies
  const { isVerboseMode } = require('./config');
  
  if (!isVerboseMode()) {
    return; // Skip logging in non-verbose mode
  }
  
  const safeConfig = { ...config };
  
  if (safeConfig.body) {
    try {
      const bodyObj = JSON.parse(safeConfig.body);
      const maskedBody = maskSensitiveData(bodyObj);
      safeConfig.body = JSON.stringify(maskedBody);
    } catch (e) {
      // If body is not JSON, mask any password-like strings
      if (typeof safeConfig.body === 'string' && safeConfig.body.includes('password')) {
        safeConfig.body = safeConfig.body.replace(/"password":"[^"]*"/, '"password":"***MASKED***"');
        safeConfig.body = safeConfig.body.replace(/"confirm_password":"[^"]*"/, '"confirm_password":"***MASKED***"');
      }
    }
  }
  
  console.log("🌍 Calling API:", endpoint, safeConfig);
}
